import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import DOMPurify from 'dompurify';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { useUnsavedChangesWarning } from '../hooks/useUnsavedChangesWarning';
import { useAuth } from '../context/AuthContext';
import { supportTaskService, storageService, getTaskStatusLabel, getTaskTypeLabel, getTaskPriorityLabel, SupportTaskStatus, SupportTaskPriority, SupportTaskType, getProjectScopeItems } from '../api';
import { getRelationTypeLabel, RELATION_TYPE_OPTIONS } from '../api/services/supportTaskService';
import { useEditorImageUpload } from '../hooks/useEditorImageUpload';
import { Button, ThemedText, ConfirmDialog, Avatar, RichTextEditor, ImageLightboxModal, MarkdownPreviewModal } from '../components';
import { SupportTaskStatusColors, SupportTaskTypeColors, SupportTaskPriorityColors, ProjectScopeColors, StatusPalette } from '../constants/statusColors';
import { PageContainer, PageHeaderRow as PageHeader } from '../components/PageStyles';
import { Card } from '../components/Card';
import { ArrowLeft, FileText, FileSpreadsheet, FileCode, FileImage, FileVideo, FileAudio, File, FileArchive, Presentation, Plus, X, Search, Link2, Ban, ShieldAlert, Copy, CopyCheck } from 'lucide-react';
import { ModalOverlay, ModalContent, ModalHeader, ModalBody } from '../components/ModalStyles';

// --- File Type Icon Mapping ---

const FILE_TYPE_MAP = {
  // Documents
  pdf: { icon: FileText, color: '#ef4444', label: 'PDF' },
  doc: { icon: FileText, color: '#2563eb', label: 'Word' },
  docx: { icon: FileText, color: '#2563eb', label: 'Word' },
  // Spreadsheets
  xls: { icon: FileSpreadsheet, color: '#16a34a', label: 'Excel' },
  xlsx: { icon: FileSpreadsheet, color: '#16a34a', label: 'Excel' },
  csv: { icon: FileSpreadsheet, color: '#16a34a', label: 'CSV' },
  // Presentations
  ppt: { icon: Presentation, color: '#ea580c', label: 'PPT' },
  pptx: { icon: Presentation, color: '#ea580c', label: 'PPT' },
  // Code
  js: { icon: FileCode, color: '#8b5cf6', label: 'Code' },
  ts: { icon: FileCode, color: '#8b5cf6', label: 'Code' },
  jsx: { icon: FileCode, color: '#8b5cf6', label: 'Code' },
  tsx: { icon: FileCode, color: '#8b5cf6', label: 'Code' },
  py: { icon: FileCode, color: '#8b5cf6', label: 'Code' },
  java: { icon: FileCode, color: '#8b5cf6', label: 'Code' },
  cs: { icon: FileCode, color: '#8b5cf6', label: 'Code' },
  cpp: { icon: FileCode, color: '#8b5cf6', label: 'Code' },
  go: { icon: FileCode, color: '#8b5cf6', label: 'Code' },
  // Config / Data
  json: { icon: FileCode, color: '#8b5cf6', label: 'JSON' },
  xml: { icon: FileCode, color: '#8b5cf6', label: 'XML' },
  yaml: { icon: FileCode, color: '#8b5cf6', label: 'YAML' },
  yml: { icon: FileCode, color: '#8b5cf6', label: 'YAML' },
  // Web
  html: { icon: FileCode, color: '#ec4899', label: 'HTML' },
  htm: { icon: FileCode, color: '#ec4899', label: 'HTML' },
  css: { icon: FileCode, color: '#ec4899', label: 'CSS' },
  scss: { icon: FileCode, color: '#ec4899', label: 'CSS' },
  // Text
  md: { icon: FileText, color: '#64748b', label: 'Markdown' },
  txt: { icon: FileText, color: '#64748b', label: 'Text' },
  rtf: { icon: FileText, color: '#64748b', label: 'RTF' },
  log: { icon: FileText, color: '#64748b', label: 'Log' },
  // Images (fallback for non-renderable)
  svg: { icon: FileImage, color: '#06b6d4', label: 'SVG' },
  // Video
  mp4: { icon: FileVideo, color: '#d946ef', label: 'Video' },
  mov: { icon: FileVideo, color: '#d946ef', label: 'Video' },
  avi: { icon: FileVideo, color: '#d946ef', label: 'Video' },
  webm: { icon: FileVideo, color: '#d946ef', label: 'Video' },
  mkv: { icon: FileVideo, color: '#d946ef', label: 'Video' },
  // Audio
  mp3: { icon: FileAudio, color: '#f59e0b', label: 'Audio' },
  wav: { icon: FileAudio, color: '#f59e0b', label: 'Audio' },
  ogg: { icon: FileAudio, color: '#f59e0b', label: 'Audio' },
  aac: { icon: FileAudio, color: '#f59e0b', label: 'Audio' },
  flac: { icon: FileAudio, color: '#f59e0b', label: 'Audio' },
  // Archives
  zip: { icon: FileArchive, color: '#78716c', label: 'Archive' },
  rar: { icon: FileArchive, color: '#78716c', label: 'Archive' },
  '7z': { icon: FileArchive, color: '#78716c', label: 'Archive' },
  tar: { icon: FileArchive, color: '#78716c', label: 'Archive' },
  gz: { icon: FileArchive, color: '#78716c', label: 'Archive' },
};

const CONTENT_TYPE_FALLBACKS = {
  video: { icon: FileVideo, color: '#d946ef', label: 'Video' },
  audio: { icon: FileAudio, color: '#f59e0b', label: 'Audio' },
  image: { icon: FileImage, color: '#06b6d4', label: 'Image' },
  text: { icon: FileText, color: '#64748b', label: 'Text' },
};

const DEFAULT_FILE_INFO = { icon: File, color: '#94a3b8', label: 'File' };

const getFileTypeIcon = (fileName, contentType) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  if (ext && FILE_TYPE_MAP[ext]) return FILE_TYPE_MAP[ext];
  if (contentType) {
    const prefix = contentType.split('/')[0];
    if (CONTENT_TYPE_FALLBACKS[prefix]) return CONTENT_TYPE_FALLBACKS[prefix];
  }
  return DEFAULT_FILE_INFO;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// --- Styled Components ---

const HeaderTitleContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: none;
  background: transparent;
  color: ${props => props.theme.colors.primary || props.theme.colors.tint};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border-radius: ${props => props.theme.borderRadius.md};
  opacity: 0.8;
  transition: all 0.2s;
  margin-left: -${props => props.theme.spacing.sm};

  &:hover {
    opacity: 1;
    background-color: ${props => props.theme.colors.primary}10;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  align-items: center;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: ${props => props.theme.spacing.xl};
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const SidebarColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;


const CardTitle = styled.h3`
  margin: 0 0 ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.large};
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.sm} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  &:last-child { border-bottom: none; }
`;

const DetailLabel = styled.span`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.sizes.default};
`;

const DetailValue = styled.span`
  color: ${props => props.theme.colors.text};
  font-weight: 500;
  font-size: ${props => props.theme.typography.sizes.default};
`;

const DescriptionContent = styled.div`
  color: ${props => props.theme.colors.text};
  line-height: 1.6;
  font-size: ${props => props.theme.typography.sizes.default};

  h1, h2, h3 { color: ${props => props.theme.colors.text}; margin: ${props => props.theme.spacing.md} 0 ${props => props.theme.spacing.sm}; }
  p { margin: ${props => props.theme.spacing.sm} 0; }
  ul, ol { padding-left: ${props => props.theme.spacing.xl}; }
  code { background: ${props => props.theme.colors.background}; padding: 2px 6px; border-radius: 4px; font-size: 0.9em; }
  pre { background: ${props => props.theme.colors.background}; padding: ${props => props.theme.spacing.md}; border-radius: ${props => props.theme.borderRadius.md}; overflow-x: auto; }
  a { color: ${props => props.theme.colors.tint}; }
  img { max-width: 100%; border-radius: ${props => props.theme.borderRadius.md}; cursor: pointer; }
`;

const EmptyText = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-style: italic;
  font-size: ${props => props.theme.typography.sizes.default};
`;

const StatusBadge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  background-color: ${props => `${props.$color}20`};
  color: ${props => props.$color};
`;

const TaskNumberBadge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 700;
  background-color: ${props => props.theme.colors.tint}20;
  color: ${props => props.theme.colors.tint};
`;

const StatusSelect = styled.select`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.small};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }

  option {
    background-color: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
  }
`;

const AttachmentsSection = styled.div`
  margin-top: ${props => props.theme.spacing.lg};
  padding-top: ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const AttachmentsTitle = styled.div`
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const AttachmentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: ${props => props.theme.spacing.md};
`;

const AttachmentThumbnail = styled.a`
  display: block;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  text-decoration: none;
  transition: box-shadow 0.2s, transform 0.15s;
  background-color: ${props => props.theme.colors.backgroundSecondary};

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }

  img {
    width: 100%;
    height: 120px;
    object-fit: cover;
    display: block;
  }
`;

const ThumbnailLabel = styled.div`
  padding: 8px 10px;
  font-size: 12px;
  color: ${props => props.theme.colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: ${props => props.theme.colors.background};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const FileIconCard = styled.a`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.15s;
  background-color: ${props => props.theme.colors.backgroundSecondary};

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }
`;

const FileIconArea = styled.div`
  width: 100%;
  height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background-color: ${props => props.$bgColor || props.theme.colors.background};
`;

const FileTypeTag = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${props => props.$color || props.theme.colors.muted};
`;

const FileSizeText = styled.div`
  font-size: 11px;
  color: ${props => props.theme.colors.muted};
`;

const AttachmentDeleteBtn = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background-color: ${props => props.theme.colors.backgroundSecondary};
  color: ${props => props.theme.colors.textSecondary};
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s, background-color 0.15s, color 0.15s;
  z-index: 2;

  &:hover {
    background-color: #ef444420;
    color: #ef4444;
  }
`;

const AttachmentCard = styled.div`
  position: relative;

  &:hover ${AttachmentDeleteBtn} {
    opacity: 1;
  }
`;

const AttachmentDropZone = styled.div`
  margin-top: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md};
  border: 2px dashed ${props => props.$isDragging ? props.theme.colors.tint : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  background-color: ${props => props.$isDragging ? `${props.theme.colors.tint}08` : 'transparent'};
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.sizes.small};

  &:hover {
    border-color: ${props => props.theme.colors.tint};
    background-color: ${props => `${props.theme.colors.tint}08`};
    color: ${props => props.theme.colors.tint};
  }
`;

const UploadingItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.tint}40;
  border-radius: ${props => props.theme.borderRadius.lg};
  color: ${props => props.theme.colors.tint};
  font-size: ${props => props.theme.typography.sizes.small};
`;

const SentryLinkContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const SentryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.tint};
  text-decoration: none;
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const ExternalLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.tint};
  text-decoration: none;
  font-size: ${props => props.theme.typography.sizes.small};

  &:hover {
    text-decoration: underline;
  }
`;

const PublicUrlContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const PublicUrlInput = styled.input`
  flex: 1;
  padding: ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.sizes.small};
  font-family: monospace;
  min-width: 0;
  cursor: text;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }
`;

const CopyButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.$copied ? props.theme.colors.tint + '20' : props.theme.colors.backgroundSecondary};
  color: ${props => props.$copied ? props.theme.colors.tint : props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.theme.colors.tint}15;
    border-color: ${props => props.theme.colors.tint};
  }
`;

const ProjectBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  background-color: ${props => `${props.$color}20`};
  color: ${props => props.$color};
`;

const ProjectBadgesContainer = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: ${props => props.theme.colors.muted};
`;

const NotFoundContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: ${props => props.theme.spacing.lg};
`;

// --- Related Tasks Styled Components ---

const RelatedTasksHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const RelatedTasksTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  background-color: ${props => props.theme.colors.tint}20;
  color: ${props => props.theme.colors.tint};
`;

const AddRelationButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: transparent;
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.sizes.small};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${props => props.theme.colors.tint};
    color: ${props => props.theme.colors.tint};
    background-color: ${props => props.theme.colors.tint}08;
  }
`;

const RelatedTasksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const RelatedTaskItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  transition: background-color 0.15s;

  &:hover {
    background-color: ${props => props.theme.colors.background};
  }
`;

const RelatedTaskLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  flex: 1;
  min-width: 0;
  text-decoration: none;
  color: inherit;
`;

const RelatedTaskTitle = styled.span`
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.small};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const RelationTypeBadge = styled.span`
  padding: 2px 6px;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  background-color: ${props => props.theme.colors.tint}15;
  color: ${props => props.theme.colors.tint};
`;

const RemoveRelationButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  flex-shrink: 0;
  opacity: 0;
  transition: all 0.15s;

  ${RelatedTaskItem}:hover & {
    opacity: 1;
  }

  &:hover {
    background-color: #ef444420;
    color: #ef4444;
  }
`;

const RelationSearchInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  padding-left: 36px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};

  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 10px;
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.textSecondary};
  pointer-events: none;
`;

const SearchResultsList = styled.div`
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-top: ${props => props.theme.spacing.sm};
`;

const SearchResultItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  cursor: pointer;
  transition: background-color 0.15s;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${props => props.theme.colors.tint}08;
  }
`;

const SearchResultTitle = styled.span`
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.small};
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RelationSelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const RelationSelectLabel = styled.label`
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  color: ${props => props.theme.colors.textSecondary};
`;

const RELATION_TYPE_CONFIG = {
  0: { icon: Link2, color: '#3b82f6', label: 'Related' },
  1: { icon: Ban, color: '#ef4444', label: 'Blocks' },
  2: { icon: ShieldAlert, color: '#f59e0b', label: 'Blocked By' },
  3: { icon: Copy, color: '#8b5cf6', label: 'Duplicate Of' },
  4: { icon: CopyCheck, color: '#8b5cf6', label: 'Duplicated By' },
};

const RelationTypeButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const RelationTypeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid ${props => props.$selected
    ? props.$color
    : props.theme.colors.border};
  background: ${props => props.$selected
    ? props.$color + '14'
    : 'transparent'};
  color: ${props => props.$selected
    ? props.$color
    : props.theme.colors.textSecondary};

  &:hover {
    border-color: ${props => props.$color};
    color: ${props => props.$color};
  }
`;

// --- Comments Styled Components ---

const CommentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const CommentItem = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md};
  background-color: transparent;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid transparent;
`;

const SystemCommentItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.xs} 0;
`;

const SystemDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.border};
  flex-shrink: 0;
`;

const SystemCommentText = styled.span`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.sizes.small};
  font-style: italic;
`;

const SystemCommentTime = styled.span`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.sizes.small};
  opacity: 0.6;
  margin-left: auto;
`;

const CommentContent = styled.div`
  flex: 1;
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const CommentAuthor = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
`;

const CommentTime = styled.span`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.sizes.small};
`;

const CommentText = styled.div`
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  line-height: 1.5;

  p { margin: 4px 0; }
  p:first-child { margin-top: 0; }
  p:last-child { margin-bottom: 0; }
  ul, ol { padding-left: 24px; }
  code { background: ${props => props.theme.colors.background}; padding: 2px 6px; border-radius: 4px; font-size: 0.9em; }
  a { color: ${props => props.theme.colors.tint}; }
  img { max-width: 100%; border-radius: 8px; cursor: pointer; margin: 4px 0; }
  blockquote { border-left: 3px solid ${props => props.theme.colors.border}; padding-left: 16px; color: ${props => props.theme.colors.muted}; margin: 8px 0; }
`;

const CommentInput = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.lg};
  padding-top: ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const CommentInputActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

// --- Color Helpers ---

const getStatusColor = (status) => SupportTaskStatusColors[status] || StatusPalette.slate;
const getTypeColor = (type) => SupportTaskTypeColors[type] || StatusPalette.slate;
const getPriorityColor = (priority) => SupportTaskPriorityColors[priority] || StatusPalette.slate;

// --- Helpers ---

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString();
  } catch {
    return dateStr;
  }
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
};

const STATUS_OPTIONS = [
  { value: SupportTaskStatus.OPEN, label: 'Open' },
  { value: SupportTaskStatus.IN_PROGRESS, label: 'In Progress' },
  { value: SupportTaskStatus.ON_HOLD, label: 'On Hold' },
  { value: SupportTaskStatus.RESOLVED, label: 'Resolved' },
  { value: SupportTaskStatus.CLOSED, label: 'Closed' },
  { value: SupportTaskStatus.BACKLOG, label: 'Backlog' },
];

const PRIORITY_OPTIONS = [
  { value: SupportTaskPriority.LOW, label: 'Low' },
  { value: SupportTaskPriority.MEDIUM, label: 'Medium' },
  { value: SupportTaskPriority.HIGH, label: 'High' },
  { value: SupportTaskPriority.CRITICAL, label: 'Critical' },
];

const TYPE_OPTIONS = [
  { value: SupportTaskType.BUG, label: 'Bug' },
  { value: SupportTaskType.FEATURE, label: 'Feature' },
  { value: SupportTaskType.IMPROVEMENT, label: 'Improvement' },
  { value: SupportTaskType.QUESTION, label: 'Question' },
  { value: SupportTaskType.PROBLEM, label: 'Problem' },
];

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['p','br','strong','b','em','i','u','s','a','img','ul','ol','li','blockquote','code','pre','h1','h2','h3','h4','h5','h6','span','div'],
  ALLOWED_ATTR: ['href','src','alt','style','target','rel','class','width','height'],
};

const sanitizeHtml = (html) => DOMPurify.sanitize(html || '', SANITIZE_CONFIG);

const stripHtmlTags = (html) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html || '';
  return (tmp.textContent || tmp.innerText || '').trim();
};

// --- Component ---

const TaskDetailPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const { isAdmin, isSuperAdmin } = useRole();
  const { user } = useAuth();

  const canEditTask = isAdmin() || !!user?.isInternalTaskOrganization;

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const hasUnsavedComment = !!stripHtmlTags(newComment);
  useUnsavedChangesWarning(hasUnsavedComment);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [publicUrlCopied, setPublicUrlCopied] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [updatingField, setUpdatingField] = useState(null); // 'status' | 'priority' | 'assignee'

  const [uploadingFiles, setUploadingFiles] = useState([]); // { id, name, progress }
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [markdownPreview, setMarkdownPreview] = useState(null);

  // Related tasks state
  const [relatedTasks, setRelatedTasks] = useState([]);
  const [showAddRelationModal, setShowAddRelationModal] = useState(false);
  const [relationSearchTerm, setRelationSearchTerm] = useState('');
  const [relationSearchResults, setRelationSearchResults] = useState([]);
  const [selectedRelationType, setSelectedRelationType] = useState(0);
  const [isSearchingRelations, setIsSearchingRelations] = useState(false);
  const [isAddingRelation, setIsAddingRelation] = useState(false);

  const loadTask = useCallback(async () => {
    try {
      setLoading(true);
      setNotFound(false);
      const data = await supportTaskService.getTaskById(id);
      setTask(data);
    } catch (error) {
      console.error('Failed to fetch task:', error);
      if (error?.status === 404 || error?.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error('Failed to load task details');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadComments = useCallback(async () => {
    try {
      setLoadingComments(true);
      const data = await supportTaskService.getComments(id);
      setComments(Array.isArray(data) ? data : (data?.items || data?.data || []));
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  }, [id]);

  const loadRelatedTasks = useCallback(async () => {
    try {
      const data = await supportTaskService.getRelatedTasks(id);
      setRelatedTasks(Array.isArray(data) ? data : (data?.items || data?.data || []));
    } catch (error) {
      console.error('Failed to fetch related tasks:', error);
      setRelatedTasks([]);
    }
  }, [id]);

  useEffect(() => {
    loadTask();
    loadComments();
    loadRelatedTasks();
  }, [loadTask, loadComments, loadRelatedTasks]);

  // Debounced search for relation modal
  const searchTimerRef = useRef(null);
  useEffect(() => {
    if (!showAddRelationModal) return;
    if (!relationSearchTerm || relationSearchTerm.trim().length < 2) {
      setRelationSearchResults([]);
      return;
    }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      try {
        setIsSearchingRelations(true);
        const data = await supportTaskService.searchTasks(relationSearchTerm.trim(), id);
        setRelationSearchResults(Array.isArray(data) ? data : (data?.items || data?.data || []));
      } catch (error) {
        console.error('Failed to search tasks:', error);
        setRelationSearchResults([]);
      } finally {
        setIsSearchingRelations(false);
      }
    }, 300);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [relationSearchTerm, showAddRelationModal, id]);

  const openLightbox = useCallback((images, index = 0) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const handleContentImageClick = useCallback((e) => {
    if (e.target.tagName === 'IMG') {
      e.preventDefault();
      const allImages = Array.from(e.currentTarget.querySelectorAll('img'));
      openLightbox(
        allImages.map(img => ({ src: img.src, alt: img.alt || '' })),
        allImages.indexOf(e.target)
      );
    }
  }, [openLightbox]);

  useEffect(() => {
    if (!canEditTask) return;
    supportTaskService.getAssignableUsers()
      .then(data => setAssignableUsers(Array.isArray(data) ? data : (data?.items || [])))
      .catch(() => setAssignableUsers([]));
  }, []);

  const handleAttachFiles = async (files) => {
    if (!task) return;
    const fileArray = Array.from(files);
    for (const file of fileArray) {
      const fileId = Date.now() + Math.random();
      setUploadingFiles(prev => [...prev, { id: fileId, name: file.name, progress: 0 }]);
      try {
        const { blobPath } = await storageService.uploadFileToBlob(file,
          (_, __, pct) => setUploadingFiles(prev => prev.map(f => f.id === fileId ? { ...f, progress: pct } : f))
        );
        await supportTaskService.addAttachment(task.id, {
          fileName: file.name,
          storagePath: blobPath,
          contentType: file.type,
          fileSize: file.size,
        });
        await loadTask();
        toast.success(`${file.name} attached`);
      } catch {
        toast.error(`Failed to attach ${file.name}`);
      } finally {
        setUploadingFiles(prev => prev.filter(f => f.id !== fileId));
      }
    }
  };

  const handleEditorImageUpload = useEditorImageUpload(
    isSuperAdmin() ? (task?.organizationId ?? null) : null
  );

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await supportTaskService.deleteAttachment(attachmentId);
      setTask(prev => ({ ...prev, attachments: prev.attachments.filter(a => a.id !== attachmentId) }));
      toast.success('Attachment removed');
    } catch {
      toast.error('Failed to remove attachment');
    }
  };

  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items || !task) return;
      const fileItems = Array.from(items).filter(i => i.kind === 'file');
      if (fileItems.length > 0) {
        handleAttachFiles(fileItems.map(i => i.getAsFile()).filter(Boolean));
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [task]); // eslint-disable-line

  const handleStatusChange = async (newStatus) => {
    if (newStatus === task.status) return;
    try {
      setUpdatingField('status');
      await supportTaskService.updateTaskStatus(task.id, { status: newStatus });
      setTask(prev => ({ ...prev, status: newStatus }));
      toast.success('Status updated');
      loadComments();
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingField(null);
    }
  };

  // Builds the full update payload from the current task (required by backend DTO)
  const buildUpdatePayload = (overrides = {}) => ({
    title: task.title,
    description: task.description,
    taskType: task.taskType,
    priority: task.priority,
    status: task.status,
    projectScope: task.projectScope,
    assignedToUserId: task.assignedToUserId ?? null,
    sentryIssueId: task.sentryIssueId ?? null,
    sentryIssueUrl: task.sentryIssueUrl ?? null,
    dueDate: task.dueDate ?? null,
    ...overrides,
  });

  const handlePriorityChange = async (newPriority) => {
    if (newPriority === task.priority) return;
    try {
      setUpdatingField('priority');
      await supportTaskService.updateTask(task.id, buildUpdatePayload({ priority: newPriority }));
      setTask(prev => ({ ...prev, priority: newPriority }));
      toast.success('Priority updated');
    } catch (err) {
      toast.error('Failed to update priority');
    } finally {
      setUpdatingField(null);
    }
  };

  const handleTaskTypeChange = async (newType) => {
    if (newType === task.taskType) return;
    try {
      setUpdatingField('taskType');
      await supportTaskService.updateTask(task.id, buildUpdatePayload({ taskType: newType }));
      setTask(prev => ({ ...prev, taskType: newType }));
      toast.success('Task type updated');
    } catch (err) {
      toast.error('Failed to update task type');
    } finally {
      setUpdatingField(null);
    }
  };

  const handleAssigneeChange = async (newUserId) => {
    const parsed = newUserId === '' ? null : parseInt(newUserId, 10);
    if (parsed === task.assignedToUserId) return;
    try {
      setUpdatingField('assignee');
      await supportTaskService.updateTask(task.id, buildUpdatePayload({ assignedToUserId: parsed }));
      const user = assignableUsers.find(u => u.id === parsed);
      const userName = user
        ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email
        : null;
      setTask(prev => ({
        ...prev,
        assignedToUserId: parsed,
        assignedToUserName: userName,
      }));
      toast.success(parsed ? 'Task assigned' : 'Assignment removed');
    } catch (err) {
      toast.error('Failed to update assignment');
    } finally {
      setUpdatingField(null);
    }
  };

  const handleAddComment = async () => {
    if (!stripHtmlTags(newComment)) return;
    try {
      setSubmittingComment(true);
      await supportTaskService.addComment(task.id, { content: newComment });
      setNewComment('');
      toast.success('Comment added');
      loadComments();
    } catch (err) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDelete = async () => {
    try {
      await supportTaskService.deleteTask(task.id);
      toast.success('Task deleted');
      navigate('/tasks');
    } catch (err) {
      toast.error('Failed to delete task');
    }
  };

  const handleBack = () => {
    navigate('/tasks');
  };

  const handleAddRelation = async (targetTaskId) => {
    try {
      setIsAddingRelation(true);
      await supportTaskService.addRelation(id, {
        targetTaskId,
        relationType: selectedRelationType,
      });
      toast.success('Related task added');
      setShowAddRelationModal(false);
      setRelationSearchTerm('');
      setRelationSearchResults([]);
      setSelectedRelationType(0);
      loadRelatedTasks();
    } catch (error) {
      console.error('Failed to add relation:', error);
      toast.error('Failed to add related task');
    } finally {
      setIsAddingRelation(false);
    }
  };

  const handleRemoveRelation = async (relationId) => {
    try {
      await supportTaskService.removeRelation(relationId);
      setRelatedTasks(prev => prev.filter(r => r.relationId !== relationId));
      toast.success('Relation removed');
    } catch (error) {
      console.error('Failed to remove relation:', error);
      toast.error('Failed to remove relation');
    }
  };

  const imageAttachments = useMemo(() =>
    (task?.attachments || [])
      .filter(a => a.contentType?.startsWith('image/') && a.fileUrl)
      .map(a => ({ src: a.fileUrl, alt: a.fileName || '', caption: a.fileName })),
    [task?.attachments]
  );

  // --- Loading State ---
  if (loading) {
    return (
      <Layout currentPath="/tasks">
        <LoadingContainer theme={theme}>
          <ThemedText>Loading task...</ThemedText>
        </LoadingContainer>
      </Layout>
    );
  }

  // --- Not Found State ---
  if (notFound || !task) {
    return (
      <Layout currentPath="/tasks">
        <PageContainer theme={theme}>
          <NotFoundContainer theme={theme}>
            <ThemedText type="title">Task Not Found</ThemedText>
            <ThemedText style={{ color: theme.colors.muted }}>
              The task you are looking for does not exist or has been removed.
            </ThemedText>
            <Button onClick={handleBack}>
              Back to Tasks
            </Button>
          </NotFoundContainer>
        </PageContainer>
      </Layout>
    );
  }

  const publicUrl = task.publicToken
    ? `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'}/api/public/tasks/${task.publicToken}`
    : null;

  const handleCopyPublicUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setPublicUrlCopied(true);
      toast.success('Public URL copied to clipboard');
      setTimeout(() => setPublicUrlCopied(false), 2000);
    } catch {
      toast.error('Failed to copy URL');
    }
  };

  const getCreatedByName = () => {
    if (task.createdByUserName) return task.createdByUserName;
    return 'Unknown';
  };

  const getAssignedToName = () => {
    if (task.assignedToUserName) return task.assignedToUserName;
    return 'Unassigned';
  };

  const getCommentAuthorName = (comment) => {
    if (comment.createdByUserName) return comment.createdByUserName;
    return 'Unknown';
  };

  return (
    <Layout currentPath="/tasks">
      <PageContainer theme={theme}>
        {/* Page Header */}
        <PageHeader theme={theme}>
          <div>
            <BackButton theme={theme} onClick={handleBack}>
              <ArrowLeft size={14} /> Back to Tasks
            </BackButton>
            <HeaderTitleContent theme={theme}>
              <ThemedText type="title" style={{ lineHeight: '1', fontSize: '24px' }}>
                {task.title}
              </ThemedText>
            </HeaderTitleContent>
          </div>
          <HeaderActions theme={theme}>
            <StatusBadge theme={theme} $color={getStatusColor(task.status)}>
              {getTaskStatusLabel(task.status)}
            </StatusBadge>
            {canEditTask && (
              <Button variant="secondary" size="sm" onClick={() => navigate(`/tasks/${id}/edit`)}>
                Edit
              </Button>
            )}
          </HeaderActions>
        </PageHeader>

        <ContentGrid theme={theme}>
          {/* Main Content (Left Column) */}
          <MainContent theme={theme}>
            {/* Description Card */}
            <Card theme={theme}>
              <CardTitle theme={theme}>Description</CardTitle>
              {task.description ? (
                <DescriptionContent theme={theme} onClick={handleContentImageClick} dangerouslySetInnerHTML={{ __html: sanitizeHtml(task.description) }} />
              ) : (
                <EmptyText theme={theme}>No description provided</EmptyText>
              )}

              {/* Task-level Attachments */}
              <AttachmentsSection theme={theme}>
                <AttachmentsTitle theme={theme}>
                  Attachments ({(task.attachments || []).length})
                </AttachmentsTitle>
                {(task.attachments || []).length > 0 && (
                  <AttachmentsGrid theme={theme}>
                    {task.attachments.map((attachment, index) => {
                      const isImage = attachment.contentType?.startsWith('image/');
                      const url = attachment.fileUrl;
                      const name = attachment.fileName || `Attachment ${index + 1}`;

                      if (isImage && url) {
                        const imgIndex = imageAttachments.findIndex(ia => ia.src === url);
                        return (
                          <AttachmentCard key={attachment.id || index}>
                            <AttachmentThumbnail
                              as="div"
                              theme={theme}
                              title={name}
                              style={{ cursor: 'pointer' }}
                              onClick={() => openLightbox(imageAttachments, Math.max(0, imgIndex))}
                            >
                              <img src={url} alt={name} loading="lazy" />
                              <ThumbnailLabel theme={theme}>{name}</ThumbnailLabel>
                            </AttachmentThumbnail>
                            {canEditTask && (
                              <AttachmentDeleteBtn
                                theme={theme}
                                title="Remove attachment"
                                onClick={() => handleDeleteAttachment(attachment.id)}
                              >
                                ×
                              </AttachmentDeleteBtn>
                            )}
                          </AttachmentCard>
                        );
                      }

                      const fileInfo = getFileTypeIcon(attachment.fileName, attachment.contentType);
                      const IconComponent = fileInfo.icon;
                      const isMd = name?.toLowerCase().endsWith('.md');

                      return (
                        <AttachmentCard key={attachment.id || index}>
                          <FileIconCard
                            href={isMd ? undefined : (url || undefined)}
                            target={isMd ? undefined : (url ? '_blank' : undefined)}
                            rel={url && !isMd ? 'noopener noreferrer' : undefined}
                            onClick={
                              isMd && url
                                ? (e) => { e.preventDefault(); setMarkdownPreview({ fileUrl: url, fileName: name }); }
                                : url ? undefined : (e) => e.preventDefault()
                            }
                            theme={theme}
                            title={name}
                            style={url ? { cursor: isMd ? 'pointer' : undefined } : { opacity: 0.6, cursor: 'default' }}
                            aria-label={`${fileInfo.label} file: ${name}`}
                          >
                            <FileIconArea theme={theme} $bgColor={`${fileInfo.color}0a`}>
                              <IconComponent size={36} color={fileInfo.color} strokeWidth={1.5} />
                              <FileTypeTag $color={fileInfo.color}>{fileInfo.label}</FileTypeTag>
                              {attachment.fileSize ? (
                                <FileSizeText theme={theme}>{formatFileSize(attachment.fileSize)}</FileSizeText>
                              ) : null}
                            </FileIconArea>
                            <ThumbnailLabel theme={theme}>{name}</ThumbnailLabel>
                          </FileIconCard>
                          {canEditTask && (
                            <AttachmentDeleteBtn
                              theme={theme}
                              title="Remove attachment"
                              onClick={() => handleDeleteAttachment(attachment.id)}
                            >
                              ×
                            </AttachmentDeleteBtn>
                          )}
                        </AttachmentCard>
                      );
                    })}
                    {uploadingFiles.map(f => (
                      <UploadingItem key={f.id} theme={theme}>
                        {f.name} — {f.progress}%
                      </UploadingItem>
                    ))}
                  </AttachmentsGrid>
                )}
                {(task.attachments || []).length === 0 && uploadingFiles.length === 0 && (
                  <div style={{ marginBottom: theme.spacing.sm }}>
                    <span style={{ color: theme.colors.textSecondary, fontSize: theme.typography.sizes.small, fontStyle: 'italic' }}>
                      No attachments yet
                    </span>
                  </div>
                )}
                {uploadingFiles.length > 0 && (task.attachments || []).length === 0 && (
                  <AttachmentsGrid theme={theme}>
                    {uploadingFiles.map(f => (
                      <UploadingItem key={f.id} theme={theme}>
                        {f.name} — {f.progress}%
                      </UploadingItem>
                    ))}
                  </AttachmentsGrid>
                )}
                {canEditTask && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      style={{ display: 'none' }}
                      onChange={(e) => { handleAttachFiles(e.target.files); e.target.value = ''; }}
                    />
                    <AttachmentDropZone
                      theme={theme}
                      $isDragging={isDragging}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={(e) => { e.preventDefault(); setIsDragging(false); handleAttachFiles(e.dataTransfer.files); }}
                    >
                      Drop files here or click to browse
                    </AttachmentDropZone>
                  </>
                )}
              </AttachmentsSection>
            </Card>

            {/* Comments Section */}
            <Card theme={theme}>
              <CardTitle theme={theme}>Comments ({comments.length})</CardTitle>

              {loadingComments ? (
                <EmptyText theme={theme}>Loading comments...</EmptyText>
              ) : comments.length === 0 ? (
                <EmptyText theme={theme}>No comments yet</EmptyText>
              ) : (
                <CommentsList theme={theme}>
                  {comments.map((comment) => {
                    const authorName = getCommentAuthorName(comment);
                    const isSystem = comment.isSystemGenerated || comment.isSystem || false;

                    if (isSystem) {
                      return (
                        <SystemCommentItem key={comment.id} theme={theme}>
                          <SystemDot theme={theme} />
                          <SystemCommentText theme={theme}>
                            {comment.content || comment.text || comment.body || ''}
                          </SystemCommentText>
                          <SystemCommentTime theme={theme}>
                            {formatDateTime(comment.createdAt)}
                          </SystemCommentTime>
                        </SystemCommentItem>
                      );
                    }

                    return (
                      <CommentItem key={comment.id} theme={theme}>
                        <Avatar name={authorName} size="md" />
                        <CommentContent theme={theme}>
                          <CommentHeader theme={theme}>
                            <CommentAuthor theme={theme}>{authorName}</CommentAuthor>
                            <CommentTime theme={theme}>
                              {formatDateTime(comment.createdAt)}
                            </CommentTime>
                          </CommentHeader>
                          <CommentText
                            theme={theme}
                            onClick={handleContentImageClick}
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(comment.content || comment.text || comment.body || '') }}
                          />
                        </CommentContent>
                      </CommentItem>
                    );
                  })}
                </CommentsList>
              )}

              {/* Add Comment Input */}
              <CommentInput theme={theme}>
                <RichTextEditor
                  value={newComment}
                  onChange={setNewComment}
                  placeholder="Write a comment..."
                  variant="compact"
                  onCtrlEnter={handleAddComment}
                  onImageUpload={handleEditorImageUpload}
                />
                <CommentInputActions>
                  <Button
                    onClick={handleAddComment}
                    disabled={!stripHtmlTags(newComment) || submittingComment}
                    size="sm"
                  >
                    {submittingComment ? 'Adding...' : 'Add Comment'}
                  </Button>
                </CommentInputActions>
              </CommentInput>
            </Card>
          </MainContent>

          {/* Sidebar (Right Column) */}
          <SidebarColumn theme={theme}>
            {/* Details Card */}
            <Card theme={theme}>
              <CardTitle theme={theme}>Details</CardTitle>

              <DetailRow theme={theme}>
                <DetailLabel theme={theme}>Task Number</DetailLabel>
                <TaskNumberBadge theme={theme}>
                  {task.taskNumber || `TASK-${task.id}`}
                </TaskNumberBadge>
              </DetailRow>

              <DetailRow theme={theme}>
                <DetailLabel theme={theme}>Status</DetailLabel>
                <DetailValue theme={theme}>
                  {canEditTask ? (
                    <StatusSelect
                      theme={theme}
                      value={task.status}
                      disabled={updatingField === 'status'}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      style={{ borderColor: getStatusColor(task.status) }}
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </StatusSelect>
                  ) : (
                    <StatusBadge theme={theme} $color={getStatusColor(task.status)}>
                      {getTaskStatusLabel(task.status)}
                    </StatusBadge>
                  )}
                </DetailValue>
              </DetailRow>

              <DetailRow theme={theme}>
                <DetailLabel theme={theme}>Type</DetailLabel>
                <DetailValue theme={theme}>
                  {canEditTask ? (
                    <StatusSelect
                      theme={theme}
                      value={task.taskType}
                      disabled={updatingField === 'taskType'}
                      onChange={(e) => handleTaskTypeChange(e.target.value)}
                      style={{ borderColor: getTypeColor(task.taskType) }}
                    >
                      {TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </StatusSelect>
                  ) : (
                    <StatusBadge theme={theme} $color={getTypeColor(task.taskType)}>
                      {getTaskTypeLabel(task.taskType)}
                    </StatusBadge>
                  )}
                </DetailValue>
              </DetailRow>

              <DetailRow theme={theme}>
                <DetailLabel theme={theme}>Priority</DetailLabel>
                <DetailValue theme={theme}>
                  {canEditTask ? (
                    <StatusSelect
                      theme={theme}
                      value={task.priority}
                      disabled={updatingField === 'priority'}
                      onChange={(e) => handlePriorityChange(e.target.value)}
                      style={{ borderColor: getPriorityColor(task.priority) }}
                    >
                      {PRIORITY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </StatusSelect>
                  ) : (
                    <StatusBadge theme={theme} $color={getPriorityColor(task.priority)}>
                      {getTaskPriorityLabel(task.priority)}
                    </StatusBadge>
                  )}
                </DetailValue>
              </DetailRow>

              <DetailRow theme={theme}>
                <DetailLabel theme={theme}>Project</DetailLabel>
                <DetailValue theme={theme}>
                  {(() => {
                    const items = getProjectScopeItems(task.projectScope);
                    if (items.length === 0) return <span style={{ color: theme.colors.muted }}>-</span>;
                    return (
                      <ProjectBadgesContainer>
                        {items.map(item => {
                          if (item === 'Portal') {
                            return <ProjectBadge key="portal" theme={theme} $color={ProjectScopeColors.Internal}>Portal</ProjectBadge>;
                          }
                          if (item === 'MobileApp') {
                            return <ProjectBadge key="mobile" theme={theme} $color={ProjectScopeColors.External}>Mobile</ProjectBadge>;
                          }
                          return null;
                        })}
                      </ProjectBadgesContainer>
                    );
                  })()}
                </DetailValue>
              </DetailRow>

              <DetailRow theme={theme}>
                <DetailLabel theme={theme}>Created By</DetailLabel>
                <DetailValue theme={theme}>{getCreatedByName()}</DetailValue>
              </DetailRow>

              <DetailRow theme={theme}>
                <DetailLabel theme={theme}>Assigned To</DetailLabel>
                <DetailValue theme={theme}>
                  {canEditTask && assignableUsers.length > 0 ? (
                    <StatusSelect
                      theme={theme}
                      value={task.assignedToUserId ?? ''}
                      disabled={updatingField === 'assignee'}
                      onChange={(e) => handleAssigneeChange(e.target.value)}
                    >
                      <option value="">Unassigned</option>
                      {assignableUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}
                        </option>
                      ))}
                    </StatusSelect>
                  ) : (
                    getAssignedToName()
                  )}
                </DetailValue>
              </DetailRow>

              <DetailRow theme={theme}>
                <DetailLabel theme={theme}>Created At</DetailLabel>
                <DetailValue theme={theme}>{formatDate(task.createdAt) || 'N/A'}</DetailValue>
              </DetailRow>

              <DetailRow theme={theme}>
                <DetailLabel theme={theme}>Due Date</DetailLabel>
                <DetailValue theme={theme}>{formatDate(task.dueDate) || 'Not set'}</DetailValue>
              </DetailRow>

              {(task.status === SupportTaskStatus.RESOLVED || task.status === SupportTaskStatus.CLOSED) && task.resolvedAt && (
                <DetailRow theme={theme}>
                  <DetailLabel theme={theme}>Resolved At</DetailLabel>
                  <DetailValue theme={theme}>{formatDate(task.resolvedAt)}</DetailValue>
                </DetailRow>
              )}
            </Card>

            {/* Sentry Link Card */}
            {task.sentryIssueId && (
              <Card theme={theme}>
                <CardTitle theme={theme}>Sentry Issue</CardTitle>
                <SentryLinkContainer theme={theme}>
                  <SentryLink theme={theme} to={`/sentry-issues/${task.sentryIssueId}`}>
                    View Issue Details
                  </SentryLink>
                  {task.sentryIssueUrl && (
                    <ExternalLink
                      theme={theme}
                      href={task.sentryIssueUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open in Sentry
                    </ExternalLink>
                  )}
                </SentryLinkContainer>
              </Card>
            )}

            {/* Public URL Card */}
            {publicUrl && (
              <Card theme={theme}>
                <CardTitle theme={theme}>Public URL</CardTitle>
                <PublicUrlContainer theme={theme}>
                  <PublicUrlInput
                    theme={theme}
                    value={publicUrl}
                    readOnly
                    onClick={(e) => e.target.select()}
                  />
                  <CopyButton
                    theme={theme}
                    $copied={publicUrlCopied}
                    onClick={handleCopyPublicUrl}
                  >
                    {publicUrlCopied ? 'Copied!' : 'Copy'}
                  </CopyButton>
                </PublicUrlContainer>
              </Card>
            )}

            {/* Related Tasks Card */}
            <Card theme={theme}>
              <RelatedTasksHeader theme={theme}>
                <RelatedTasksTitle theme={theme}>
                  <CardTitle theme={theme} style={{ margin: 0 }}>Related Tasks</CardTitle>
                  <CountBadge theme={theme}>{relatedTasks.length}</CountBadge>
                </RelatedTasksTitle>
                {canEditTask && (
                  <AddRelationButton
                    theme={theme}
                    onClick={() => setShowAddRelationModal(true)}
                  >
                    <Plus size={14} />
                    Add
                  </AddRelationButton>
                )}
              </RelatedTasksHeader>
              {relatedTasks.length > 0 ? (
                <RelatedTasksList theme={theme}>
                  {relatedTasks.map((rel) => (
                    <RelatedTaskItem key={rel.relationId} theme={theme}>
                      <RelatedTaskLink theme={theme} to={`/tasks/${rel.taskId}`}>
                        <TaskNumberBadge theme={theme} style={{ fontSize: '10px', padding: '2px 6px' }}>
                          {rel.taskNumber ?? '?'}
                        </TaskNumberBadge>
                        <RelatedTaskTitle theme={theme}>
                          {(rel.title ?? '').length > 40
                            ? (rel.title ?? '').slice(0, 40) + '...'
                            : (rel.title ?? '')}
                        </RelatedTaskTitle>
                        <RelationTypeBadge theme={theme}>
                          {rel.relationTypeLabel ?? getRelationTypeLabel(rel.relationType)}
                        </RelationTypeBadge>
                      </RelatedTaskLink>
                      {canEditTask && (
                        <RemoveRelationButton
                          theme={theme}
                          title="Remove relation"
                          onClick={(e) => { e.stopPropagation(); handleRemoveRelation(rel.relationId); }}
                        >
                          <X size={12} />
                        </RemoveRelationButton>
                      )}
                    </RelatedTaskItem>
                  ))}
                </RelatedTasksList>
              ) : (
                <EmptyText theme={theme}>No related tasks</EmptyText>
              )}
            </Card>

            {/* Actions Card */}
            {(canEditTask || isSuperAdmin()) && (
              <Card theme={theme}>
                <CardTitle theme={theme}>Actions</CardTitle>
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                  {canEditTask && (
                    <Button
                      variant="secondary"
                      onClick={() => navigate(`/tasks/${id}/edit`)}
                      style={{ width: '100%' }}
                    >
                      Edit Task
                    </Button>
                  )}
                  {isSuperAdmin() && (
                    <Button
                      variant="danger"
                      onClick={() => setDeleteConfirmOpen(true)}
                      style={{ width: '100%' }}
                    >
                      Delete Task
                    </Button>
                  )}
                </div>
              </Card>
            )}
          </SidebarColumn>
        </ContentGrid>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={deleteConfirmOpen}
          title="Delete Task"
          message="Are you sure you want to delete this task? This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={handleDelete}
          onCancel={() => setDeleteConfirmOpen(false)}
        />

        {/* Image Lightbox */}
        <ImageLightboxModal
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          images={lightboxImages}
          initialIndex={lightboxIndex}
        />

        <MarkdownPreviewModal
          isOpen={!!markdownPreview}
          onClose={() => setMarkdownPreview(null)}
          fileUrl={markdownPreview?.fileUrl}
          fileName={markdownPreview?.fileName}
        />

        {/* Add Relation Modal */}
        <ModalOverlay
          theme={theme}
          $isOpen={showAddRelationModal}
          onClick={() => { setShowAddRelationModal(false); setRelationSearchTerm(''); setRelationSearchResults([]); setSelectedRelationType(0); }}
        >
          <ModalContent theme={theme} onClick={(e) => e.stopPropagation()}>
            <ModalHeader theme={theme}>
              <CardTitle theme={theme} style={{ margin: 0 }}>Add Related Task</CardTitle>
              <button
                onClick={() => { setShowAddRelationModal(false); setRelationSearchTerm(''); setRelationSearchResults([]); setSelectedRelationType(0); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.textSecondary, padding: '4px' }}
              >
                <X size={18} />
              </button>
            </ModalHeader>
            <ModalBody theme={theme}>
              <RelationSelectWrapper theme={theme}>
                <RelationSelectLabel theme={theme}>Relation Type</RelationSelectLabel>
                <RelationTypeButtonGroup>
                  {RELATION_TYPE_OPTIONS.map((opt) => {
                    const cfg = RELATION_TYPE_CONFIG[opt.value];
                    const Icon = cfg.icon;
                    return (
                      <RelationTypeButton
                        key={opt.value}
                        theme={theme}
                        $selected={selectedRelationType === opt.value}
                        $color={cfg.color}
                        onClick={() => setSelectedRelationType(opt.value)}
                        type="button"
                      >
                        <Icon size={13} />
                        {opt.label}
                      </RelationTypeButton>
                    );
                  })}
                </RelationTypeButtonGroup>
              </RelationSelectWrapper>
              <RelationSelectWrapper theme={theme}>
                <RelationSelectLabel theme={theme}>Search Tasks</RelationSelectLabel>
                <SearchInputWrapper>
                  <SearchIconWrapper theme={theme}>
                    <Search size={16} />
                  </SearchIconWrapper>
                  <RelationSearchInput
                    theme={theme}
                    placeholder="Search by title or task number..."
                    value={relationSearchTerm}
                    onChange={(e) => setRelationSearchTerm(e.target.value)}
                    autoFocus
                  />
                </SearchInputWrapper>
              </RelationSelectWrapper>
              {isSearchingRelations && (
                <EmptyText theme={theme}>Searching...</EmptyText>
              )}
              {!isSearchingRelations && relationSearchResults.length > 0 && (
                <SearchResultsList theme={theme}>
                  {relationSearchResults.map((result) => (
                    <SearchResultItem
                      key={result.id}
                      theme={theme}
                      onClick={() => !isAddingRelation && handleAddRelation(result.id)}
                      style={isAddingRelation ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                    >
                      <TaskNumberBadge theme={theme} style={{ fontSize: '10px', padding: '2px 6px', flexShrink: 0 }}>
                        {result.taskNumber || `TASK-${result.id}`}
                      </TaskNumberBadge>
                      <SearchResultTitle theme={theme}>
                        {result.title}
                      </SearchResultTitle>
                      <StatusBadge theme={theme} $color={getStatusColor(result.status)} style={{ flexShrink: 0, fontSize: '10px' }}>
                        {getTaskStatusLabel(result.status)}
                      </StatusBadge>
                      <StatusBadge theme={theme} $color={getPriorityColor(result.priority)} style={{ flexShrink: 0, fontSize: '10px' }}>
                        {getTaskPriorityLabel(result.priority)}
                      </StatusBadge>
                    </SearchResultItem>
                  ))}
                </SearchResultsList>
              )}
              {!isSearchingRelations && relationSearchTerm.trim().length >= 2 && relationSearchResults.length === 0 && (
                <EmptyText theme={theme}>No tasks found</EmptyText>
              )}
              {!isSearchingRelations && relationSearchTerm.trim().length < 2 && relationSearchTerm.trim().length > 0 && (
                <EmptyText theme={theme}>Type at least 2 characters to search</EmptyText>
              )}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      </PageContainer>
    </Layout>
  );
};

export default TaskDetailPage;
export { TaskDetailPage };
